import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  Row,
  Col,
  Card,
  Select,
  Button,
  Progress,
  Table,
  Tag,
  Typography,
  Spin,
  Badge,
  Space,
  Empty,
} from 'antd';
import {
  ReloadOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  ShoppingCartOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
  RiseOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { PageTitle } from '@app/components/common/PageTitle/PageTitle';
import { BaseChart } from '@app/components/common/charts/BaseChart';
import {
  getDashboardMetrics,
  DashboardMetricsResponse,
} from '@app/apis/dashboard-api';
import { notificationController } from '@app/controllers/notificationController';

const { Text, Title } = Typography;
const { Option } = Select;

// ==========================================
// STYLED COMPONENTS (Executive SaaS Design)
// ==========================================

const DashboardContainer = styled.div`
  padding: 20px 24px 40px;
  background: #f4f6f9;
  min-height: calc(100vh - 80px);
`;

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeaderTitles = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainTitle = styled(Title)`
  &.ant-typography {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const Subtitle = styled(Text)`
  color: #64748b;
  font-size: 13px;
  margin-top: 2px;
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledCard = styled(Card)`
  border-radius: 14px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background: #ffffff;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  }

  .ant-card-head {
    border-bottom: 1px solid #f1f5f9;
    padding: 14px 20px;
    min-height: auto;
  }

  .ant-card-head-title {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
  }

  .ant-card-body {
    padding: 20px;
  }
`;

const KpiCard = styled(StyledCard)`
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) => props.color || '#3b82f6'};
  }
`;

const KpiCardContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const KpiIconWrapper = styled.div<{ background: string }>`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: ${(props) => props.background};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
`;

const KpiValue = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
  margin: 8px 0 4px;
  letter-spacing: -0.5px;
`;

const KpiLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const KpiFooter = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CircularCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 10px 0;
`;

const CircleTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-top: 16px;
`;

const CircleSubtitle = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
  max-width: 220px;
`;

const RankBadge = styled.span<{ rank: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => (props.rank <= 3 ? '#ffffff' : '#64748b')};
  background: ${(props) => {
    if (props.rank === 1) return 'linear-gradient(135deg, #f59e0b, #d97706)';
    if (props.rank === 2) return 'linear-gradient(135deg, #94a3b8, #64748b)';
    if (props.rank === 3) return 'linear-gradient(135deg, #b45309, #78350f)';
    return '#f1f5f9';
  }};
`;

export const NftDashboardPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState<string>('all');
  const [metrics, setMetrics] = useState<DashboardMetricsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchMetrics = useCallback(
    async (range: string) => {
      setLoading(true);
      try {
        const data = await getDashboardMetrics(range);
        setMetrics(data);
      } catch (err: any) {
        notificationController.error({
          message: err?.message || 'Failed to fetch dashboard metrics',
        });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchMetrics(timeRange);
  }, [timeRange, fetchMetrics]);

  // ==========================================
  // ECHARTS OPTIONS
  // ==========================================

  // Revenue & Orders Timeline Area Chart
  const revenueTrendOption = useMemo(() => {
    if (!metrics?.timeline?.length) return {};

    const dates = metrics.timeline.map((item) => item.date);
    const directRev = metrics.timeline.map((item) => item.directRevenue);
    const walletDep = metrics.timeline.map((item) => item.rechargeAmount);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        borderColor: '#334155',
        borderWidth: 1,
        textStyle: { color: '#f8fafc', fontSize: 13 },
        formatter: (params: any) => {
          let str = `<div style="font-weight:700;margin-bottom:6px;border-bottom:1px solid #475569;padding-bottom:4px">${params[0]?.axisValue}</div>`;
          params.forEach((p: any) => {
            str += `<div style="display:flex;justify-content:space-between;gap:18px;margin:3px 0;">
              <span>${p.marker} ${p.seriesName}</span>
              <span style="font-weight:700">₹ ${p.value.toLocaleString('en-IN')}</span>
            </div>`;
          });
          return str;
        },
      },
      legend: {
        data: ['Direct Orders Revenue', 'Wallet Recharges'],
        top: 0,
        right: 10,
        textStyle: { color: '#475569', fontSize: 12 },
      },
      grid: {
        left: '2%',
        right: '2%',
        bottom: '3%',
        top: '12%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLine: { lineStyle: { color: '#cbd5e1' } },
        axisLabel: { color: '#64748b', fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#64748b',
          formatter: (value: number) => `₹${value >= 1000 ? `${value / 1000}k` : value}`,
        },
        splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
      },
      series: [
        {
          name: 'Direct Orders Revenue',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: directRev,
          itemStyle: { color: '#0ea5e9' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(14, 165, 233, 0.45)' },
                { offset: 1, color: 'rgba(14, 165, 233, 0.02)' },
              ],
            },
          },
        },
        {
          name: 'Wallet Recharges',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: walletDep,
          itemStyle: { color: '#8b5cf6' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(139, 92, 246, 0.45)' },
                { offset: 1, color: 'rgba(139, 92, 246, 0.02)' },
              ],
            },
          },
        },
      ],
    };
  }, [metrics]);

  // Payment Method Split Donut Chart
  const paymentMixDonutOption = useMemo(() => {
    if (!metrics?.kpis) return {};

    const directCount = metrics.kpis.directCount || 0;
    const walletCount = metrics.kpis.walletOrdersCount || 0;

    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: <b>{c} orders</b> ({d}%)',
      },
      legend: {
        bottom: '0%',
        left: 'center',
        textStyle: { color: '#475569', fontSize: 12 },
      },
      color: ['#0ea5e9', '#8b5cf6'],
      series: [
        {
          name: 'Payment Mix',
          type: 'pie',
          radius: ['52%', '78%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '14',
              fontWeight: 'bold',
              formatter: '{b}\n{d}%',
            },
          },
          labelLine: { show: false },
          data: [
            { value: directCount, name: 'Direct (Gateway)' },
            { value: walletCount, name: 'Wallet Payments' },
          ],
        },
      ],
    };
  }, [metrics]);

  // Helper values
  const kpis = metrics?.kpis;
  const deliveries = metrics?.deliveries;
  const walletPct =
    kpis?.totalOrdersCount && kpis.totalOrdersCount > 0
      ? Math.round((kpis.walletOrdersCount / kpis.totalOrdersCount) * 100)
      : 50;

  // Table Columns
  const topProductColumns = [
    {
      title: '#',
      dataIndex: 'rank',
      key: 'rank',
      width: 50,
      render: (rank: number) => <RankBadge rank={rank}>{rank}</RankBadge>,
    },
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <span style={{ fontWeight: 600, color: '#1e293b' }}>{name}</span>,
    },
    {
      title: 'Units Sold',
      dataIndex: 'totalQty',
      key: 'totalQty',
      align: 'center' as const,
      render: (qty: number) => <Tag color="cyan">{qty} units</Tag>,
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'right' as const,
      render: (rev: number) => (
        <span style={{ fontWeight: 700, color: '#0f172a' }}>
          ₹ {Number(rev || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
  ];

  const recentOrderColumns = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 90,
      render: (id: any) => <Tag color="blue">#{id}</Tag>,
    },
    {
      title: 'Customer',
      dataIndex: 'clientName',
      key: 'clientName',
      render: (name: string, record: any) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>{name}</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>{record.phone}</div>
        </div>
      ),
    },
    {
      title: 'Method',
      dataIndex: 'payment_method',
      key: 'payment_method',
      align: 'center' as const,
      render: (method: string) => (
        <Tag color={method === 'wallet' ? 'purple' : 'geekblue'}>
          {method === 'wallet' ? 'Wallet' : 'Direct'}
        </Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      render: (amount: number) => (
        <span style={{ fontWeight: 700, color: '#0f172a' }}>
          ₹ {Number(amount || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status: string) => {
        let color = 'default';
        if (status === 'Completed') color = 'success';
        else if (status === 'Pending') color = 'warning';
        else if (status === 'Cancelled' || status === 'Refunded') color = 'error';
        return <Tag color={color}>{status}</Tag>;
      },
    },
  ];

  return (
    <DashboardContainer>
      <PageTitle>Operations Dashboard</PageTitle>

      {/* HEADER CONTROLS */}
      <HeaderBar>
        <HeaderTitles>
          <MainTitle level={2}>
            Operations Dashboard
            <Badge status="processing" color="#10b981" text={<span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>LIVE</span>} />
          </MainTitle>
          <Subtitle>Real-time revenue, order metrics, and daily delivery fulfillment</Subtitle>
        </HeaderTitles>

        <ControlsGroup>
          <Select
            value={timeRange}
            onChange={(val) => setTimeRange(val)}
            style={{ width: 170 }}
            size="middle"
          >
            <Option value="today">Today</Option>
            <Option value="7days">Last 7 Days</Option>
            <Option value="month">This Month</Option>
            <Option value="year">This Year</Option>
            <Option value="all">All Time</Option>
          </Select>

          <Button
            type="primary"
            icon={<ReloadOutlined spin={loading} />}
            onClick={() => fetchMetrics(timeRange)}
          >
            Refresh
          </Button>
        </ControlsGroup>
      </HeaderBar>

      <Spin spinning={loading} tip="Loading analytics...">
        {/* ROW 1: PRIMARY KPI STAT CARDS */}
        <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
          {/* KPI 1: Realized Revenue */}
          <Col xs={24} sm={12} lg={6}>
            <KpiCard color="#10b981">
              <KpiCardContent>
                <div>
                  <KpiLabel>Realized Net Revenue</KpiLabel>
                  <KpiValue>
                    ₹ {(kpis?.totalRealizedRevenue || 0).toLocaleString('en-IN')}
                  </KpiValue>
                </div>
                <KpiIconWrapper background="linear-gradient(135deg, #10b981, #059669)">
                  <DollarCircleOutlined />
                </KpiIconWrapper>
              </KpiCardContent>
              <KpiFooter>
                <span>Direct + Wallet Deposits</span>
                <Tag color="green">Excl. Cancelled</Tag>
              </KpiFooter>
            </KpiCard>
          </Col>

          {/* KPI 2: Wallet Recharges */}
          <Col xs={24} sm={12} lg={6}>
            <KpiCard color="#8b5cf6">
              <KpiCardContent>
                <div>
                  <KpiLabel>Wallet Recharges</KpiLabel>
                  <KpiValue>
                    ₹ {(kpis?.walletRechargeAmount || 0).toLocaleString('en-IN')}
                  </KpiValue>
                </div>
                <KpiIconWrapper background="linear-gradient(135deg, #8b5cf6, #6366f1)">
                  <WalletOutlined />
                </KpiIconWrapper>
              </KpiCardContent>
              <KpiFooter>
                <span>{kpis?.walletRechargeCount || 0} Successful Deposits</span>
                <Tag color="purple">Wallet Balance</Tag>
              </KpiFooter>
            </KpiCard>
          </Col>

          {/* KPI 3: Direct Orders */}
          <Col xs={24} sm={12} lg={6}>
            <KpiCard color="#0ea5e9">
              <KpiCardContent>
                <div>
                  <KpiLabel>Direct Non-Wallet Orders</KpiLabel>
                  <KpiValue>
                    ₹ {(kpis?.directRevenue || 0).toLocaleString('en-IN')}
                  </KpiValue>
                </div>
                <KpiIconWrapper background="linear-gradient(135deg, #0ea5e9, #0284c7)">
                  <ShoppingCartOutlined />
                </KpiIconWrapper>
              </KpiCardContent>
              <KpiFooter>
                <span>{kpis?.directCount || 0} Direct Orders</span>
                <Tag color="blue">{100 - walletPct}% Share</Tag>
              </KpiFooter>
            </KpiCard>
          </Col>

          {/* KPI 4: Active Subscriptions */}
          <Col xs={24} sm={12} lg={6}>
            <KpiCard color="#f59e0b">
              <KpiCardContent>
                <div>
                  <KpiLabel>Active Subscriptions</KpiLabel>
                  <KpiValue>{kpis?.activeSubscriptions || 0}</KpiValue>
                </div>
                <KpiIconWrapper background="linear-gradient(135deg, #f59e0b, #d97706)">
                  <SyncOutlined />
                </KpiIconWrapper>
              </KpiCardContent>
              <KpiFooter>
                <span>{kpis?.totalSubscriptions || 0} Total Plans</span>
                <Tag color="gold">Recurring Daily</Tag>
              </KpiFooter>
            </KpiCard>
          </Col>
        </Row>

        {/* ROW 2: CIRCULAR METRIC CARDS */}
        <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
          {/* Circle 1: Today's Delivery Fulfillment */}
          <Col xs={24} md={8}>
            <StyledCard>
              <CircularCardWrapper>
                <Progress
                  type="circle"
                  percent={deliveries?.fulfillmentRate ?? 0}
                  width={130}
                  strokeWidth={9}
                  strokeColor={
                    (deliveries?.fulfillmentRate ?? 0) === 100
                      ? { '0%': '#10b981', '100%': '#059669' }
                      : (deliveries?.fulfillmentRate ?? 0) > 0
                      ? { '0%': '#0ea5e9', '100%': '#3b82f6' }
                      : { '0%': '#94a3b8', '100%': '#64748b' }
                  }
                  format={(percent) => (
                    <div style={{ color: '#0f172a', fontWeight: 800 }}>
                      {percent ?? 0}%
                      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>
                        {(percent ?? 0) === 100
                          ? 'FULFILLED'
                          : (percent ?? 0) > 0
                          ? 'PROGRESS'
                          : 'PENDING'}
                      </div>
                    </div>
                  )}
                />
                <CircleTitle>Today's Delivery Fulfillment</CircleTitle>
                <CircleSubtitle>
                  <b>{deliveries?.todayDelivered || 0}</b> delivered out of{' '}
                  <b>{deliveries?.todayTotal || 0}</b> scheduled deliveries today
                </CircleSubtitle>
                <Space style={{ marginTop: 12 }}>
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    {deliveries?.todayDelivered || 0} Done
                  </Tag>
                  <Tag icon={<ClockCircleOutlined />} color="warning">
                    {deliveries?.todayScheduled || 0} Scheduled
                  </Tag>
                  {deliveries && deliveries.todayCancelled > 0 && (
                    <Tag icon={<CloseCircleOutlined />} color="error">
                      {deliveries.todayCancelled} Cancelled
                    </Tag>
                  )}
                </Space>
              </CircularCardWrapper>
            </StyledCard>
          </Col>

          {/* Circle 2: Payment Method Volume Share */}
          <Col xs={24} md={8}>
            <StyledCard>
              <CircularCardWrapper>
                <Progress
                  type="circle"
                  percent={walletPct}
                  width={130}
                  strokeWidth={9}
                  strokeColor={{
                    '0%': '#8b5cf6',
                    '100%': '#6366f1',
                  }}
                  format={(percent) => (
                    <div style={{ color: '#0f172a', fontWeight: 800 }}>
                      {percent}%
                      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>
                        WALLET
                      </div>
                    </div>
                  )}
                />
                <CircleTitle>Wallet vs Direct Orders</CircleTitle>
                <CircleSubtitle>
                  <b>{kpis?.walletOrdersCount || 0}</b> orders paid with wallet (₹
                  {(kpis?.walletOrdersSpent || 0).toLocaleString('en-IN')}) vs{' '}
                  <b>{kpis?.directCount || 0}</b> direct orders
                </CircleSubtitle>
                <Space style={{ marginTop: 12 }}>
                  <Tag color="purple">{walletPct}% Wallet</Tag>
                  <Tag color="blue">{100 - walletPct}% Gateway</Tag>
                </Space>
              </CircularCardWrapper>
            </StyledCard>
          </Col>

          {/* Circle 3: Customer Base Engagement */}
          <Col xs={24} md={8}>
            <StyledCard>
              <CircularCardWrapper>
                <Progress
                  type="circle"
                  percent={kpis?.clientActiveRatio || 0}
                  width={130}
                  strokeWidth={9}
                  strokeColor={{
                    '0%': '#0ea5e9',
                    '100%': '#3b82f6',
                  }}
                  format={(percent) => (
                    <div style={{ color: '#0f172a', fontWeight: 800 }}>
                      {percent}%
                      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>
                        ACTIVE
                      </div>
                    </div>
                  )}
                />
                <CircleTitle>Customer Base Health</CircleTitle>
                <CircleSubtitle>
                  <b>{kpis?.activeClients || 0}</b> active ordering clients out of{' '}
                  <b>{kpis?.totalClients || 0}</b> registered accounts
                </CircleSubtitle>
                <Space style={{ marginTop: 12 }}>
                  <Tag color="cyan">{kpis?.activeClients || 0} Active</Tag>
                  <Tag color="default">{kpis?.totalClients || 0} Total</Tag>
                </Space>
              </CircularCardWrapper>
            </StyledCard>
          </Col>
        </Row>

        {/* ROW 3: CHARTS */}
        <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
          {/* Chart 1: Revenue Timeline Area Chart */}
          <Col xs={24} lg={16}>
            <StyledCard title="Revenue Growth & Daily Deposit Trend">
              {metrics?.timeline?.length ? (
                <BaseChart option={revenueTrendOption} height={330} />
              ) : (
                <Empty description="No transaction history in this date range" style={{ padding: '60px 0' }} />
              )}
            </StyledCard>
          </Col>

          {/* Chart 2: Payment Distribution Donut Chart */}
          <Col xs={24} lg={8}>
            <StyledCard title="Order Payment Breakdown">
              {kpis?.totalOrdersCount ? (
                <BaseChart option={paymentMixDonutOption} height={330} />
              ) : (
                <Empty description="No orders placed yet" style={{ padding: '60px 0' }} />
              )}
            </StyledCard>
          </Col>
        </Row>

        {/* ROW 4: DATA TABLES */}
        <Row gutter={[20, 20]}>
          {/* Table 1: Top Selling Products */}
          <Col xs={24} lg={12}>
            <StyledCard
              title={
                <Space>
                  <TrophyOutlined style={{ color: '#f59e0b' }} />
                  <span>Top Performing Products</span>
                </Space>
              }
            >
              <Table
                columns={topProductColumns}
                dataSource={metrics?.topProducts || []}
                rowKey="name"
                pagination={false}
                size="small"
                locale={{ emptyText: 'No product sales recorded' }}
              />
            </StyledCard>
          </Col>

          {/* Table 2: Recent Orders Stream */}
          <Col xs={24} lg={12}>
            <StyledCard
              title={
                <Space>
                  <RiseOutlined style={{ color: '#10b981' }} />
                  <span>Recent Live Orders</span>
                </Space>
              }
            >
              <Table
                columns={recentOrderColumns}
                dataSource={metrics?.recentOrders || []}
                rowKey="_id"
                pagination={false}
                size="small"
                locale={{ emptyText: 'No recent orders' }}
              />
            </StyledCard>
          </Col>
        </Row>
      </Spin>
    </DashboardContainer>
  );
};

export default NftDashboardPage;
